import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    date: String,
    url: String,
    mode: { type: String, default: "create" }
  }

  connect() {
    this.element.style.position = "relative"
    this.createIndicator()

    if (this.modeValue === "create") {
      this.element.style.cursor = "crosshair"
    } else {
      this.element.style.cursor = "pointer"
    }
  }

  createIndicator() {
    this.indicator = document.createElement("div")
    this.indicator.className = "absolute left-0 right-0 bg-primary text-primary-content text-xs px-1 py-0.5 rounded pointer-events-none hidden z-20 text-center opacity-90 w-16 mx-auto"
    this.element.appendChild(this.indicator)
  }

  mousemove(event) {
    if (this.modeValue !== "create") return

    const { timeString, y } = this.calculateTime(event)

    this.indicator.textContent = timeString
    this.indicator.style.top = `${y - 10}px` // Slightly above cursor
    this.indicator.classList.remove("hidden")

    this.currentTimestamp = `${this.dateValue}T${timeString}:00`
  }

  calculateTime(event) {
    const rect = this.element.getBoundingClientRect()
    const y = event.clientY - rect.top
    const height = rect.height

    // Assume 06:00 to 22:00 (16 hours) for better usability
    const startHour = 6
    const endHour = 22
    const totalHours = endHour - startHour

    const percentage = Math.max(0, Math.min(1, y / height))
    const hourFloat = startHour + (percentage * totalHours)

    const hour = Math.floor(hourFloat)
    const minute = Math.floor((hourFloat - hour) * 60)

    // Round to nearest 15 min
    const roundedMinute = Math.round(minute / 15) * 15

    let displayHour = hour
    let displayMinute = roundedMinute
    if (displayMinute === 60) {
      displayHour++
      displayMinute = 0
    }

    const timeString = `${displayHour.toString().padStart(2, '0')}:${displayMinute.toString().padStart(2, '0')}`
    return { timeString, y }
  }

  dragover(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const { timeString, y } = this.calculateTime(event)
    this.indicator.textContent = timeString
    this.indicator.style.top = `${y - 10}px`
    this.indicator.classList.remove("hidden")
  }

  dragleave(event) {
    this.indicator.classList.add("hidden")
  }

  async drop(event) {
    event.preventDefault()
    this.indicator.classList.add("hidden")

    const updateUrl = event.dataTransfer.getData("application/x-update-url")
    if (!updateUrl) return

    const { timeString } = this.calculateTime(event)
    const newStartAt = `${this.dateValue}T${timeString}:00`

    const durationSeconds = parseInt(event.dataTransfer.getData("application/x-duration"))
    let newEndAt = null

    if (durationSeconds) {
      const startDate = new Date(newStartAt)
      const endDate = new Date(startDate.getTime() + durationSeconds * 1000)

      const endYear = endDate.getFullYear()
      const endMonth = (endDate.getMonth() + 1).toString().padStart(2, '0')
      const endDay = endDate.getDate().toString().padStart(2, '0')
      const endHour = endDate.getHours().toString().padStart(2, '0')
      const endMinute = endDate.getMinutes().toString().padStart(2, '0')

      newEndAt = `${endYear}-${endMonth}-${endDay}T${endHour}:${endMinute}:00`
    }

    const body = {
      user_time_slot: {
        start_at: newStartAt,
        ...(newEndAt && { end_at: newEndAt })
      }
    }

    try {
      const response = await fetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(body)
      })

      if (response.ok) {
        window.location.reload()
      }
    } catch (error) {
      console.error("Error updating time slot:", error)
    }
  }

  mouseleave() {
    this.indicator.classList.add("hidden")
  }

  click(event) {
    // Ignore clicks on existing events (which are links)
    if (event.target.closest('a')) return

    if (this.modeValue === "navigate") {
      window.location.href = this.urlValue
      return
    }

    if (!this.currentTimestamp) return

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("start_at", this.currentTimestamp)

    // Set default end time to 4 hours later
    const startDate = new Date(this.currentTimestamp)
    startDate.setHours(startDate.getHours() + 4)

    // Format end date as ISO string without timezone issues (simple approach)
    const endYear = startDate.getFullYear()
    const endMonth = (startDate.getMonth() + 1).toString().padStart(2, '0')
    const endDay = startDate.getDate().toString().padStart(2, '0')
    const endHour = startDate.getHours().toString().padStart(2, '0')
    const endMinute = startDate.getMinutes().toString().padStart(2, '0')

    const endTimeString = `${endYear}-${endMonth}-${endDay}T${endHour}:${endMinute}:00`
    url.searchParams.set("end_at", endTimeString)

    window.location.href = url.toString()
  }
}
